import { useState, useCallback } from 'react';
import { uploadImage, uploadVideo } from '../utils/cloudinary';

type MediaType = 'image' | 'video';
type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface UseCloudinaryUploadResult {
  upload: (file: File) => Promise<string>;
  mediaUrl: string;
  status: UploadStatus;
  error: string | null;
  progress: number;
  reset: () => void;
}

/**
 * A custom hook for handling Cloudinary uploads
 * @param type The type of media to upload ('image' or 'video')
 * @returns Object containing upload function, status, and result data
 */
export function useCloudinaryUpload(type: MediaType): UseCloudinaryUploadResult {
  const [mediaUrl, setMediaUrl] = useState<string>('');
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  const upload = useCallback(
    async (file: File): Promise<string> => {
      setStatus('uploading');
      setProgress(0);
      setError(null);
      
      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setProgress((prev) => {
            const newProgress = prev + Math.random() * 10;
            return newProgress > 90 ? 90 : newProgress;
          });
        }, 300);
        
        // Perform the actual upload
        const url = type === 'image' 
          ? await uploadImage(file)
          : await uploadVideo(file);
        
        clearInterval(progressInterval);
        setProgress(100);
        setMediaUrl(url);
        setStatus('success');
        return url;
      } catch (err) {
        setStatus('error');
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
        throw err;
      }
    },
    [type]
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setMediaUrl('');
    setError(null);
    setProgress(0);
  }, []);

  return {
    upload,
    mediaUrl,
    status,
    error,
    progress,
    reset
  };
} 